<script setup lang="ts">
const colorMode = useColorMode()
const color = computed(() => colorMode.value === 'dark' ? '#041d27' : 'white')

const siteUrl = 'https://factorix.app'

/*
 * Acá NO va texto de marketing, y no por gusto.
 *
 * Antes había un título y una descripción fijos en español —que además decían «24
 * módulos especializados» y «empresas hondureñas»—, y al ser globales salían en
 * TODA página, incluidas las de `/en/`. Eso es justo lo que lee un buscador y lo
 * que se ve al compartir el enlace.
 *
 * Traducirlos acá no se puede: `useI18n()` en `app.vue` rompe el prerender (este
 * componente queda fuera del contexto de i18n). Y tampoco hace falta: las ocho
 * páginas definen su propio SEO, y ya sale en el idioma correcto porque viene del
 * contenido del locale o de las claves de i18n.
 *
 * Queda sólo la marca, que es igual en los dos idiomas.
 */
const title = 'Factorix'
const ogImage = `${siteUrl}/og-image`

/*
 * `lang`, `hreflang` y `canonical` los arma i18n según la ruta.
 *
 * Antes acá había `htmlAttrs: { lang: 'es' }` fijo —así `/en/` salía declarándose
 * en español— y, peor, un `canonical` global apuntando a la PORTADA. Eso le decía
 * a Google que `/modulos`, `/signup`, `/socios` y las 27 fichas de módulo eran
 * duplicados del inicio, o sea que no valía la pena indexarlas. Con el inglés en
 * `/en/` el mismo canonical lo habría dejado invisible.
 *
 * `useLocaleHead` pone el `lang` correcto, los `hreflang` entre idiomas y el
 * canonical de CADA página.
 */
const localeHead = useLocaleHead()

useHead(() => ({
  meta: [
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { key: 'theme-color', name: 'theme-color', content: color.value },
    ...(localeHead.value.meta ?? []),
  ],
  link: [
    { rel: 'icon', type: 'image/png', href: '/favicon.png' },
    ...(localeHead.value.link ?? []),
  ],
  htmlAttrs: { lang: localeHead.value.htmlAttrs?.lang ?? 'es' },
}))

useSeoMeta({
  // Sólo el default: las ocho páginas ya traen su título completo, con la marca
  // incluida. Un `titleTemplate` que agregara «— Factorix» lo duplicaba.
  title,
  author: 'Factorix — Asefiscohn + Insolution',
  ogType: 'website',
  ogImage,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageType: 'image/svg+xml',
  ogUrl: siteUrl,
  ogSiteName: 'Factorix',
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterImage: ogImage,
  robots: 'index, follow',
})

// Load JotForm chat after 3 seconds or first user interaction
onMounted(() => {
  const events = ['scroll', 'mousemove', 'touchstart', 'keydown']
  let loaded = false

  function onInteraction() {
    if (loaded) return
    loaded = true
    events.forEach(e => window.removeEventListener(e, onInteraction))
    loadJotForm()
  }

  events.forEach(e => window.addEventListener(e, onInteraction, { once: true, passive: true }))
  setTimeout(() => onInteraction(), 3000)
})

let jotFormLoaded = false
function loadJotForm() {
  if (jotFormLoaded) return
  jotFormLoaded = true
  const s = document.createElement('script')
  s.src = 'https://eu.jotform.com/agent/embedjs/019d17a6e9707edd828aab82782bfa3764c7/embed.js'
  s.async = true
  s.onerror = () => console.warn('JotForm chat widget failed to load')
  document.body.appendChild(s)
}

// JSON-LD structured data
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Factorix',
        // Sin `description`: la que había estaba en español y fija, así que en
        // `/en/` el dato estructurado decía otra cosa que la página.
        url: siteUrl,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'HNL',
          description: 'Plan base gratuito con facturación electrónica',
        },
        author: {
          '@type': 'Organization',
          name: 'Asefiscohn + Insolution',
          url: siteUrl,
        },
      }),
    },
  ],
})
</script>

<template>
  <UApp>
    <AppHeader />
    <UMain>
      <NuxtPage />
    </UMain>
    <LazyAppFooter />
  </UApp>
</template>
